import * as React from "react";
import { useState, useEffect } from "react";
import { environment } from "src/environments/environment";
import { Corporate, Project } from "../models";
import { USER_ROLES } from "../enums";
import { fetchMetaData } from "src/app/polar-SAAS-Components/api-helper";
import authService from "src/app/polar-SAAS-Components/auth-service";
import { useIsMobile } from "src/app/polar-SAAS-Components/utilsNew";


interface UserData {
  lastName: string;
  email: string;
  mobile: string;
  whatsApp: string;
}

type ActiveTab = "corporate" | "project" | "kits";

interface CreateUserProps {
  onClose: () => void;
  onCreateUser: (userData: UserData, entityId: string, userRoleId?: number) => void;
  projects: Project[];
  corporates: Corporate[];
  activeTab: ActiveTab;
}

type CountryCode = { code: string; name: string };

const NAME_PATTERN = /^[a-zA-Z\s]+$/;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const DIGITS_ONLY_PATTERN = /^\d*$/;
const PHONE_LENGTH_PATTERN = /^\d{9,15}$/;

const isDigitsOnly = (val: string) => DIGITS_ONLY_PATTERN.test(val);
const isPhoneLengthValid = (val: string) => PHONE_LENGTH_PATTERN.test(val);

const validateRequiredPhone = (val: string, label = "Phone number") => {
  if (!val.trim()) return `${label} is required`;
  if (!isDigitsOnly(val)) return `${label} must contain only digits`;
  if (!isPhoneLengthValid(val)) return `${label} must be between 9 and 15 digits`;
  return "";
};

const validateOptionalPhone = (val: string, label = "WhatsApp number") => {
  if (!val.trim()) return "";
  if (!isDigitsOnly(val)) return `${label} must contain only digits`;
  if (!isPhoneLengthValid(val)) return `${label} must be between 9 and 15 digits`;
  return "";
};

const getRoleOptions = (activeTab: ActiveTab) => {
  switch (activeTab) {
    case "corporate":
      return USER_ROLES.filter((role) => role.userRoleId === 100);
    case "project":
      return USER_ROLES.filter(
        (role) =>
          role.userRoleId === 1000 ||
          role.userRoleId === 2000 ||
          role.userRoleId === 3000 ||
          role.userRoleId === 4000 ||
          role.userRoleId === 5000 ||
          role.userRoleId === 6000
      );
    case "kits":
      return USER_ROLES.filter((role) => role.userRoleId === 10000);
    default:
      return [];
  }
};

const CreateUser: React.FC<CreateUserProps> = ({ onClose, onCreateUser, projects, corporates, activeTab }) => {
  const [selectedEntity, setSelectedEntity] = useState<string>("");
  const [selectedRoleId, setSelectedRoleId] = useState<number>(0);
  const [formData, setFormData] = useState<UserData>({ lastName: "", email: "", mobile: "", whatsApp: "" });
  const isMobile =useIsMobile(768);


  const selectedProject = projects.find((project) => project.id === selectedEntity);
  const numberOfLevels = selectedProject ? selectedProject.maintainLevel : 0;

  const [errors, setErrors] = useState({
    lastName: "",
    email: "",
    mobile: "",
    whatsApp: "",
    entity: "",
    role: "",
  });
  const [loading, setLoading] = useState(false);

  const [countryCodes, setCountryCodes] = useState<CountryCode[]>([]);
  const [codesLoading, setCodesLoading] = useState(true);
  const [selectedCountryCode, setSelectedCountryCode] = useState("+94");
  const [selectedWhatsAppCountryCode, setSelectedWhatsAppCountryCode] = useState("+94");

  const roleOptions = getRoleOptions(activeTab);

  useEffect(() => {
    if (activeTab === "corporate" && corporates.length > 0 && !selectedEntity) {
      setSelectedEntity(corporates[0].id);
      setSelectedRoleId(roleOptions[0].userRoleId || 0);
    } else if (activeTab === "project" && projects.length > 0 && !selectedEntity) {
      setSelectedEntity(projects[0].id);
      setSelectedRoleId(roleOptions[0].userRoleId || 0);
    } else if (activeTab === "kits" && !selectedRoleId && roleOptions.length > 0) {
      setSelectedRoleId(roleOptions[0].userRoleId);
    }
  }, [corporates, projects, activeTab, selectedEntity, selectedRoleId, roleOptions]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const cacheKey = "countryCodesCache_v1";
        const cached = localStorage.getItem(cacheKey);
        if (cached) {
          const { at, data } = JSON.parse(cached) as { at: number; data: CountryCode[] };
          if (Date.now() - at < 24 * 60 * 60 * 1000) {
            if (!cancelled) {
              setCountryCodes(data);
              setCodesLoading(false);
            }
            return;
          }
        }
        const res = await fetch("https://restcountries.com/v3.1/all?fields=name,idd");
        if (!res.ok) throw new Error("Failed to fetch country codes");
        type RC = { name?: { common?: string }; idd?: { root?: string; suffixes?: string[] } };
        const raw: RC[] = await res.json();

        const map = new Map<string, string>();
        for (const c of raw) {
          const root = c.idd.root;
          const suffixes = c.idd.suffixes;
          const countryName = c.name.common;
          if (!root || !countryName) continue;
          if (Array.isArray(suffixes) && suffixes.length > 0) {
            for (const s of suffixes) {
              const code = `${root}${s}`;
              if (!map.has(code)) map.set(code, countryName);
            }
          } else {
            const code = root;
            if (!map.has(code)) map.set(code, countryName);
          }
        }

        let arr: CountryCode[] = Array.from(map.entries()).map(([code, name]) => ({
          code,
          name: `${name} (${code})`,
        }));

        const PRIORITY = new Set(["+94", "+91", "+1", "+44"]);
        arr.sort((a, b) => {
          const ap = PRIORITY.has(a.code) ? 0 : 1;
          const bp = PRIORITY.has(b.code) ? 0 : 1;
          if (ap !== bp) return ap - bp;
          return a.name.localeCompare(b.name);
        });

        if (!cancelled) {
          setCountryCodes(arr);
          localStorage.setItem(cacheKey, JSON.stringify({ at: Date.now(), data: arr }));
        }
      } catch {
        if (!cancelled) {
          setCountryCodes([
            { code: "+94", name: "Sri Lanka (+94)" },
            { code: "+91", name: "India (+91)" },
            { code: "+1", name: "USA (+1)" },
            { code: "+44", name: "UK (+44)" },
          ]);
        }
      } finally {
        if (!cancelled) setCodesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if ((name === "mobile" || name === "whatsApp") && value && !DIGITS_ONLY_PATTERN.test(value)) return;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name as keyof typeof errors]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const getDynamicRoleOptions = (activeTab: ActiveTab) => {
    if (activeTab === "corporate") {
      return USER_ROLES.filter((role) => role.userRoleId === 100);
    } else if (activeTab === "project") {
      let roles = [];
      if(!authService.isAzureUser()){  roles = [...roles, ...USER_ROLES.filter((role) => role.userRoleId === 1000)];}
      if (numberOfLevels > 0) {
        const maintenanceRoles = USER_ROLES.filter((role) => role.userRoleId >= 2000 && role.userRoleId < 7000).slice(
          0,
          numberOfLevels
        );
        roles = [...roles, ...maintenanceRoles];
      }
      return roles;
    } else if (activeTab === "kits") {
      return USER_ROLES.filter((role) => role.userRoleId === 10000);
    }
    return [];
  };

  const handleEntityChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedEntity(e.target.value);
    if (errors.entity) setErrors((prev) => ({ ...prev, entity: "" }));
  };

  const handleRoleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedRoleId(Number(e.target.value));
    if (errors.role) setErrors((prev) => ({ ...prev, role: "" }));
  };

  const handleCountryCodeChange = (e: React.ChangeEvent<HTMLSelectElement>, type: "mobile" | "whatsApp") => {
    if (type === "mobile") setSelectedCountryCode(e.target.value);
    else setSelectedWhatsAppCountryCode(e.target.value);
  };

  const validateForm = () => {
    let valid = true;
    const newErrors = { lastName: "", email: "", mobile: "", whatsApp: "", entity: "", role: "" };

    if (activeTab !== "kits" && !selectedEntity) {
      newErrors.entity = `${activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} is required`;
      valid = false;
    }
    if (!selectedRoleId) {
      newErrors.role = "Role is required";
      valid = false;
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = "Name is required";
      valid = false;
    } else if (formData.lastName.trim().length < 2) {
      newErrors.lastName = "Name must be at least 2 characters";
      valid = false;
    } else if (formData.lastName.trim().length > 50) {
      newErrors.lastName = "Name cannot exceed 50 characters";
      valid = false;
    } else if (!NAME_PATTERN.test(formData.lastName.trim())) {
      newErrors.lastName = "Name can only contain letters and spaces";
      valid = false;
    }
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
      valid = false;
    } else if (!EMAIL_PATTERN.test(formData.email)) {
      newErrors.email = "Invalid email format";
      valid = false;
    } else if (formData.email.length > 100) {
      newErrors.email = "Email cannot exceed 100 characters";
      valid = false;
    }

    const mobileErr = validateRequiredPhone(formData.mobile, "Phone number");
    if (mobileErr) {
      newErrors.mobile = mobileErr;
      valid = false;
    }

    const waErr = validateOptionalPhone(formData.whatsApp, "WhatsApp number");
    if (waErr) {
      newErrors.whatsApp = waErr;
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const data = await fetchMetaData();
    const baseUrl = environment.host.substring(0, environment.host.lastIndexOf("/"));
    const hostUrl = (data && data.configuration && data.configuration.configHost) || `${baseUrl}/sign-up`;
    if (validateForm()) {
      const userDataWithFullWhatsapp = {
        ...formData,
        host: hostUrl,
        whatsApp: formData.whatsApp ? `${selectedWhatsAppCountryCode}${formData.whatsApp}` : "",
      };
      setLoading(true);
      onCreateUser(userDataWithFullWhatsapp, selectedEntity, selectedRoleId);
      setLoading(false);
    }
  };

  const entities = activeTab === "corporate" ? corporates : projects;
  const entityLabel = activeTab === "kits" ? "" : activeTab.charAt(0).toUpperCase() + activeTab.slice(1);

  const selectBase: React.CSSProperties = {
    width: "100px",
    padding: "10px 8px",
    height: "42px",
    lineHeight: "20px",
    borderRadius: "4px",
    fontSize: "14px",
    fontWeight: 400,
    outline: "none",
    fontFamily: "'Poppins', sans-serif",
    boxSizing: "border-box",
    color: "#324054",
    backgroundColor: "#f5f6f6ff",
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(50, 64, 84, 0.4)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 1000,
        fontFamily: "'Poppins', sans-serif",
      }}
    >
      <div
        style={{
          backgroundColor: "#FFFFFF",
          width: isMobile ? "95%" : "703px",
          minWidth: "300px",
          overflowX: "auto",
          borderRadius: "2px",
          boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            backgroundColor: "#FFFFF",
            padding: "12px 32px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            minWidth:"max-content"
          }}
        >
          <h2
            style={{
              fontSize: "20px",
              fontWeight: 600,
              color: "#324054",
              fontFamily: "'Poppins', sans-serif",
              margin: 0,
            }}
          >
            ADD {activeTab.toUpperCase()} USER
          </h2>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              fontSize: "24px",
              color: "#324054",
              fontWeight: "bold",
              lineHeight: 1,
            }}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div style={{ backgroundColor: "#f5f6f6ff", padding: "22px" }}>
          <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "12px",minWidth: "290px" }}>
            {activeTab !== "kits" && (
              <div>
                <label
                  style={{
                    display: "block",
                    textAlign: "left",
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#324054",
                    marginBottom: "6px",
                  }}
                >
                  {entityLabel}
                </label>
                <select
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    border: `1px solid ${errors.entity ? "#EF4444" : "#E5E7EB"}`,
                    borderRadius: "4px",
                    fontSize: "14px",
                    fontWeight: 400,
                    outline: "none",
                    fontFamily: "'Poppins', sans-serif",
                    boxSizing: "border-box",
                    color: "#324054",
                    backgroundColor: "#f5f6f6ff",
                  }}
                  value={selectedEntity}
                  onChange={handleEntityChange}
                >
                  <option value="">Select {entityLabel}</option>
                  {(entities as Project[]).map((entity) => (
                    <option key={entity.id} value={entity.id}>
                      {entity.name}
                    </option>
                  ))}
                </select>
                {errors.entity && (
                  <span style={{ color: "#EF4444", fontSize: "12px", marginTop: "4px", display: "block", textAlign: "left" }}>
                    {errors.entity}
                  </span>
                )}
              </div>
            )}

            <div>
              <label
                style={{
                  display: "block",
                  textAlign: "left",
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "#324054",
                  marginBottom: "6px",
                }}
              >
                Email
              </label>
              <input
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: `1px solid ${errors.email ? "#EF4444" : "#E5E7EB"}`,
                  borderRadius: "4px",
                  fontSize: "14px",
                  fontWeight: 400,
                  outline: "none",
                  fontFamily: "'Poppins', sans-serif",
                  boxSizing: "border-box",
                  color: "#324054",
                  backgroundColor: "#f5f6f6ff",
                }}
                type="email"
                name="email"
                placeholder="Enter email here"
                value={formData.email}
                onChange={handleChange}
              />
              {errors.email && (
                <span style={{ color: "#EF4444", fontSize: "12px", marginTop: "4px", display: "block", textAlign: "left" }}>
                  {errors.email}
                </span>
              )}
            </div>

            <div>
              <label
                style={{
                  display: "block",
                  textAlign: "left",
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "#324054",
                  marginBottom: "6px",
                }}
              >
                Name
              </label>
              <input
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: `1px solid ${errors.lastName ? "#EF4444" : "#E5E7EB"}`,
                  borderRadius: "4px",
                  fontSize: "14px",
                  fontWeight: 400,
                  outline: "none",
                  fontFamily: "'Poppins', sans-serif",
                  boxSizing: "border-box",
                  color: "#324054",
                  backgroundColor: "#f5f6f6ff",
                }}
                type="text"
                name="lastName"
                placeholder="Enter name here"
                value={formData.lastName}
                onChange={handleChange}
              />
              {errors.lastName && (
                <span style={{ color: "#EF4444", fontSize: "12px", marginTop: "4px", display: "block", textAlign: "left" }}>
                  {errors.lastName}
                </span>
              )}
            </div>

            <div style={{ display: "flex", gap: "16px", flexDirection: isMobile ? "column" : "row" }}>
              <div style={{ flex: 1 }}>
                <label
                  style={{
                    display: "block",
                    textAlign: "left",
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#324054",
                    marginBottom: "6px",
                  }}
                >
                  Phone No.
                </label>
                <div style={{ display: "flex", gap: "8px" }}>
                  <select
                    style={{
                      ...selectBase,
                      border: `1px solid ${errors.mobile ? "#EF4444" : "#E5E7EB"}`,
                    }}
                    value={selectedCountryCode}
                    onChange={(e) => handleCountryCodeChange(e, "mobile")}
                    disabled={codesLoading}
                  >
                    {codesLoading ? (
                      <option>Loading…</option>
                    ) : (
                      countryCodes.map((country) => (
                        <option key={country.code} value={country.code}>
                          {country.code}
                        </option>
                      ))
                    )}
                  </select>
                  <input
                    style={{
                      flex: 1,
                      padding: "10px 12px",
                      border: `1px solid ${errors.mobile ? "#EF4444" : "#E5E7EB"}`,
                      borderRadius: "4px",
                      fontSize: "14px",
                      fontWeight: 400,
                      outline: "none",
                      fontFamily: "'Poppins', sans-serif",
                      boxSizing: "border-box",
                      color: "#324054",
                      backgroundColor: "#f5f6f6ff",
                    }}
                    type="tel"
                    name="mobile"
                    placeholder="07x xx xx xxx"
                    value={formData.mobile}
                    onChange={handleChange}
                    pattern="[0-9]*"
                    inputMode="numeric"
                    maxLength={15}
                  />
                </div>
                {errors.mobile && (
                  <span style={{ color: "#EF4444", fontSize: "12px", marginTop: "4px", display: "block", textAlign: "left" }}>
                    {errors.mobile}
                  </span>
                )}
              </div>

              <div style={{ flex: 1 }}>
                <label
                  style={{
                    display: "block",
                    textAlign: "left",
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#324054",
                    marginBottom: "6px",
                  }}
                >
                  WhatsApp No.
                </label>
                <div style={{ display: "flex", gap: "8px" }}>
                  <select
                    style={{
                      ...selectBase,
                      border: `1px solid ${errors.whatsApp ? "#EF4444" : "#E5E7EB"}`,
                    }}
                    value={selectedWhatsAppCountryCode}
                    onChange={(e) => handleCountryCodeChange(e, "whatsApp")}
                    disabled={codesLoading}
                  >
                    {codesLoading ? (
                      <option>Loading…</option>
                    ) : (
                      countryCodes.map((country) => (
                        <option key={country.code} value={country.code}>
                          {country.code}
                        </option>
                      ))
                    )}
                  </select>
                  <input
                    style={{
                      flex: 1,
                      padding: "10px 12px",
                      border: `1px solid ${errors.whatsApp ? "#EF4444" : "#E5E7EB"}`,
                      borderRadius: "4px",
                      fontSize: "14px",
                      fontWeight: 400,
                      outline: "none",
                      fontFamily: "'Poppins', sans-serif",
                      boxSizing: "border-box",
                      color: "#324054",
                      backgroundColor: "#f5f6f6ff",
                    }}
                    type="tel"
                    name="whatsApp"
                    placeholder="07x xx xx xxx"
                    value={formData.whatsApp}
                    onChange={handleChange}
                    pattern="[0-9]*"
                    inputMode="numeric"
                    maxLength={15}
                  />
                </div>
                {errors.whatsApp && (
                  <span style={{ color: "#EF4444", fontSize: "12px", marginTop: "4px", display: "block", textAlign: "left" }}>
                    {errors.whatsApp}
                  </span>
                )}
              </div>
            </div>

            {activeTab !== "corporate" && (
              <div>
                <label
                  style={{
                    display: "block",
                    textAlign: "left",
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#324054",
                    marginBottom: "6px",
                  }}
                >
                  Role
                </label>
                <select
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    border: `1px solid ${errors.role ? "#EF4444" : "#E5E7EB"}`,
                    borderRadius: "4px",
                    fontSize: "14px",
                    fontWeight: 400,
                    outline: "none",
                    fontFamily: "'Poppins', sans-serif",
                    boxSizing: "border-box",
                    color: "#324054",
                    backgroundColor: "#f5f6f6ff",
                  }}
                  value={selectedRoleId}
                  onChange={handleRoleChange}
                >
                  <option value="">Select Role</option>
                  {getDynamicRoleOptions(activeTab).map((role) => (
                    <option key={role.userRoleId} value={role.userRoleId}>
                      {role.roleName}
                    </option>
                  ))}
                </select>
                {errors.role && (
                  <span style={{ color: "#EF4444", fontSize: "12px", marginTop: "4px", display: "block", textAlign: "left" }}>
                    {errors.role}
                  </span>
                )}
              </div>
            )}

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "12px", marginTop: "24px" }}>
              <button
                type="button"
                style={{
                  padding: "10px 16px",
                  borderRadius: "4px",
                  fontWeight: 500,
                  fontSize: "14px",
                  cursor: "pointer",
                  minWidth: "120px",
                  fontFamily: "'Poppins', sans-serif",
                  backgroundColor: "#FFFFFF",
                  color: "#324054",
                  border: "1px solid #324054",
                }}
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                type="submit"
                style={{
                  padding: "10px 16px",
                  borderRadius: "4px",
                  fontWeight: 500,
                  fontSize: "14px",
                  cursor: "pointer",
                  minWidth: "120px",
                  fontFamily: "'Poppins', sans-serif",
                  backgroundColor: "#2A7CED",
                  color: "#FFFFFF",
                  border: "none",
                  opacity: loading ? 0.7 : 1,
                }}
                disabled={loading}
              >
                {loading ? "Creating..." : "Create User"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateUser;
